/**
 * Token Storage
 *
 * Handles secure storage of authentication tokens.
 * Uses localStorage for persistence across sessions.
 */

const ACCESS_TOKEN_KEY = "ridetofive-admin-access-token";
const REFRESH_TOKEN_KEY = "ridetofive-admin-refresh-token";

/**
 * Check if we're in a browser environment
 */
const isBrowser = (): boolean => typeof window !== "undefined";

/**
 * Token storage utility for managing authentication tokens
 */
export const tokenStorage = {
    /**
     * Store the access token
     */
    setAccessToken(token: string): void {
        if (!isBrowser()) return;
        window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
    },

    /**
     * Retrieve the access token
     */
    getAccessToken(): string | null {
        if (!isBrowser()) return null;
        return window.localStorage.getItem(ACCESS_TOKEN_KEY);
    },

    /**
     * Store the refresh token
     */
    setRefreshToken(token: string): void {
        if (!isBrowser()) return;
        window.localStorage.setItem(REFRESH_TOKEN_KEY, token);
    },

    /**
     * Retrieve the refresh token
     */
    getRefreshToken(): string | null {
        if (!isBrowser()) return null;
        return window.localStorage.getItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Store both tokens at once
     */
    setTokens(accessToken: string, refreshToken?: string): void {
        this.setAccessToken(accessToken);
        if (refreshToken) {
            this.setRefreshToken(refreshToken);
        }
    },

    /**
     * Clear all tokens (logout)
     */
    clearTokens(): void {
        if (!isBrowser()) return;
        window.localStorage.removeItem(ACCESS_TOKEN_KEY);
        window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Check if user has a stored token
     */
    hasToken(): boolean {
        const token = this.getAccessToken();
        return typeof token === "string" && token.trim().length > 0;
    },
};

export default tokenStorage;
